import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Pause,
  Play,
  Star,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Countdown } from '@/components/delivery/countdown';
import { ProductCard } from '@/components/product/product-card';
import { Button } from '@/components/ui/button';
import { cn, formatDateKey } from '@/lib/utils';
import type {
  CampaignSlide,
  CampaignSliderSection,
  CategoryGridSection,
  DeliveryAvailability,
  EditorialSection,
  HeroSection,
  InstagramSection,
  IntentSection,
  OccasionSection,
  ProductRailSection,
  ReviewsSection,
  TrustSection,
} from '@/types/storefront';

/**
 * ANA SAYFA BÖLÜMLERİ
 *
 * Her bölüm kasıtlı olarak FARKLI bir kalıp kullanır: tam genişlik fotoğraf,
 * dar editorial metin, yatay ray, asimetrik grid. Aynı kart kabının üst üste
 * tekrarlanması "hazır tema" hissinin ana kaynağıdır ve bilinçle kaçınılır
 * (PRODUCT-DECISIONS §2).
 */

// --- Hero ------------------------------------------------------------------

export function Hero({
  section,
  delivery,
}: {
  section: HeroSection;
  delivery: DeliveryAvailability | null;
}) {
  return (
    <section className="relative">
      <div className="relative h-[86svh] min-h-[520px] w-full overflow-hidden sm:h-[78svh] lg:h-[88svh]">
        {section.imageUrl ? (
          <img
            src={section.imageUrl}
            alt={section.imageAlt ?? ''}
            // Hero LCP adayıdır: tek eager görsel.
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-surface-sunken" aria-hidden />
        )}

        {/*
          Ağır gradient ile metni "kurtarmak" yerine, metin alt bölgeye
          yerleştirilir ve yalnızca o bölgede ince bir yumuşama uygulanır.
        */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-ink/55 to-transparent"
        />

        <div className="container-page absolute inset-x-0 bottom-0 pb-10 sm:pb-14">
          <div className="max-w-xl">
            {section.subtitle ? (
              <p className="text-xs uppercase tracking-wide text-white/85">
                {section.subtitle}
                {delivery?.sameDay.eligible ? (
                  <>
                    {' · '}
                    <Countdown
                      cutoffAt={delivery.sameDay.cutoffAt}
                      className="text-white"
                    />
                  </>
                ) : null}
              </p>
            ) : null}

            <h1 className="mt-3 font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-5xl">
              {section.title ?? 'Bugün birini mutlu edin.'}
            </h1>

            {section.ctaLabel && section.ctaHref ? (
              <Button asChild variant="onImage" size="lg" className="mt-7">
                <Link to={section.ctaHref}>
                  {section.ctaLabel}
                  <ArrowRight className="size-4" aria-hidden />
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Niyet şeridi ----------------------------------------------------------

export function Intent({ section }: { section: IntentSection }) {
  return (
    <section className="mt-16 sm:mt-24" aria-labelledby="intent-title">
      <div className="container-page">
        <h2
          id="intent-title"
          className="font-serif text-2xl text-ink sm:text-3xl"
        >
          {section.title ?? 'Ne için gönderiyorsunuz?'}
        </h2>
      </div>

      {/* Mobilde yatay kayan şerit, masaüstünde ızgara */}
      <div className="container-page mt-6">
        {/*
          `.rail` yardımcı sınıfı display:flex kurduğu için md:grid ile
          birlikte kullanılmaz (aynı katmanda çakışır). Sınıflar burada
          açıkça yazılır: mobilde kayan şerit, md üstünde ızgara.
        */}
        <ul
          className={cn(
            'flex gap-3 overflow-x-auto',
            '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            'md:grid md:grid-cols-4 md:overflow-visible lg:grid-cols-8',
          )}
        >
          {section.items.map((item) => (
            <li
              key={item.label}
              className="w-[42%] flex-none min-[420px]:w-[38%] md:w-auto"
            >
              <Link
                to={item.href}
                className={cn(
                  'group relative flex h-full items-end overflow-hidden rounded-lg',
                  'aspect-[4/3] md:aspect-[3/4]',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700',
                  // Görsel yoksa kart sade çerçeveli hâline döner.
                  item.imageUrl
                    ? 'border border-transparent'
                    : 'border border-line bg-surface transition-colors duration-base ease-flow hover:border-leaf-300 hover:bg-leaf-50',
                )}
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    loading="lazy"
                    width={600}
                    height={800}
                    className={cn(
                      'absolute inset-0 size-full object-cover',
                      // Fotoğraf kraldır: hover'da hafifçe yaklaşır.
                      'transition-transform duration-slow ease-flow',
                      'group-hover:scale-[1.04] motion-reduce:transform-none',
                    )}
                  />
                ) : null}

                <span
                  className={cn(
                    'relative p-4 font-serif text-base leading-snug',
                    'transition-transform duration-base ease-flow group-hover:translate-x-0.5',
                    // Görsel üzerinde beyaz metin; altındaki perde SVG'de.
                    item.imageUrl
                      ? 'text-white [text-shadow:0_1px_8px_rgb(28_26_23/0.45)]'
                      : 'text-ink',
                  )}
                >
                  {item.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// --- Kampanya vitrini ------------------------------------------------------

/** Otomatik geçiş aralığı. Okumaya vakit bırakacak kadar uzun. */
const CAMPAIGN_AUTOPLAY_MS = 6000;
/** Kullanıcı dokunduktan sonra otomatik geçişin geri gelmesi için sessizlik. */
const CAMPAIGN_RESUME_MS = 12000;

/**
 * KAMPANYA SLIDER'I
 *
 * Hangi kampanyanın görüneceğine sunucu karar verir (tarih, aktiflik, banner
 * varlığı). Burada yalnızca sunum vardır.
 *
 * KÜTÜPHANE YOK: kaydırma tarayıcının kendi scroll-snap'idir. Böylece mobil
 * swipe, ivme ve erişilebilirlik bedavaya gelir; 30 kB'lık bir carousel
 * paketi ana sayfaya girmez.
 *
 * TEK KAMPANYA: ok, nokta ve otomatik geçiş hiç basılmaz — tek bir premium
 * banner kalır. Kontroller yalnızca gerçekten gezinilecek bir şey varken var.
 */
export function CampaignSlider({ section }: { section: CampaignSliderSection }) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  /** Hover/focus ya da dokunma sonrası geçici duraklama. */
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef<number | null>(null);

  const slides = section.slides;
  const isCarousel = slides.length > 1;
  const label = section.title ?? 'Kampanyalar';

  /** Aktif slide, kaydırma konumundan okunur — ayrı bir durum kaynağı yok. */
  const syncActive = useCallback(() => {
    const track = trackRef.current;

    if (!track || track.clientWidth === 0) return;

    setActive(Math.round(track.scrollLeft / track.clientWidth));
  }, []);

  const goTo = useCallback((index: number, smooth = true) => {
    const track = trackRef.current;

    if (!track) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    track.scrollTo({
      left: index * track.clientWidth,
      behavior: smooth && !prefersReducedMotion ? 'smooth' : 'auto',
    });
  }, []);

  /**
   * Kullanıcı etkileşimi otomatik geçişi geçici durdurur; bir süre sessizlik
   * olursa kendiliğinden geri döner. "Bir kez dokundu, bir daha oynamaz"
   * davranışı sayfayı ölü gösteriyordu.
   */
  const holdAutoplay = useCallback(() => {
    setPaused(true);

    if (resumeTimer.current) {
      window.clearTimeout(resumeTimer.current);
    }

    resumeTimer.current = window.setTimeout(
      () => setPaused(false),
      CAMPAIGN_RESUME_MS,
    );
  }, []);

  useEffect(() => () => {
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
  }, []);

  // Otomatik geçiş: tek slide, azaltılmış hareket tercihi ya da duraklama
  // varsa hiç kurulmaz.
  useEffect(() => {
    if (!isCarousel || !autoplay || paused) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = window.setInterval(() => {
      const track = trackRef.current;

      if (!track) return;

      // Sekme arkadayken döndürmek hem gereksiz hem de pil yakar.
      if (document.hidden) return;

      const next = (Math.round(track.scrollLeft / track.clientWidth) + 1) % slides.length;
      goTo(next);
    }, CAMPAIGN_AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [autoplay, goTo, isCarousel, paused, slides.length]);

  // Pencere yeniden boyutlanınca slide genişliği değişir; hizayı korur.
  useEffect(() => {
    const onResize = () => goTo(active, false);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [active, goTo]);

  const trackId = `campaign-slider-${section.key}`;

  return (
    <section
      className="mt-16 sm:mt-24"
      // Ekran okuyucu bunun bir karusel olduğunu ve kaç öğe olduğunu bilmeli.
      role="region"
      aria-roledescription={isCarousel ? 'karusel' : undefined}
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={(event) => {
        if (!isCarousel) return;

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          holdAutoplay();
          goTo(Math.min(active + 1, slides.length - 1));
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          holdAutoplay();
          goTo(Math.max(active - 1, 0));
        }
      }}
    >
      <div className="container-page">
        <div className="relative">
          <ul
            id={trackId}
            ref={trackRef}
            onScroll={syncActive}
            onPointerDown={holdAutoplay}
            onTouchStart={holdAutoplay}
            className={cn(
              'flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain',
              '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              'rounded-lg',
            )}
          >
            {slides.map((slide, index) => (
              <li
                key={slide.id}
                className="w-full flex-none snap-center"
                role="group"
                aria-roledescription={isCarousel ? 'slayt' : undefined}
                aria-label={isCarousel ? `${index + 1} / ${slides.length}` : undefined}
              >
                <CampaignBanner slide={slide} priority={index === 0} />
              </li>
            ))}
          </ul>

          {/*
            Kontroller yalnızca gezinilecek birden fazla kampanya varken
            basılır: tek kampanya "boş carousel" gibi görünmez.
          */}
          {isCarousel ? (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 right-0 hidden items-center justify-between px-3 sm:flex">
                <button
                  type="button"
                  aria-label="Önceki kampanya"
                  aria-controls={trackId}
                  disabled={active === 0}
                  onClick={() => {
                    holdAutoplay();
                    goTo(active - 1);
                  }}
                  className={cn(
                    'pointer-events-auto inline-flex size-10 items-center justify-center rounded-full',
                    'bg-white/85 text-ink shadow-soft backdrop-blur-md',
                    'transition-colors duration-fast hover:bg-white',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                    'disabled:pointer-events-none disabled:opacity-0',
                  )}
                >
                  <ChevronLeft className="size-5" aria-hidden />
                </button>

                <button
                  type="button"
                  aria-label="Sonraki kampanya"
                  aria-controls={trackId}
                  disabled={active === slides.length - 1}
                  onClick={() => {
                    holdAutoplay();
                    goTo(active + 1);
                  }}
                  className={cn(
                    'pointer-events-auto inline-flex size-10 items-center justify-center rounded-full',
                    'bg-white/85 text-ink shadow-soft backdrop-blur-md',
                    'transition-colors duration-fast hover:bg-white',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                    'disabled:pointer-events-none disabled:opacity-0',
                  )}
                >
                  <ChevronRight className="size-5" aria-hidden />
                </button>
              </div>

              <div className="mt-4 flex items-center justify-center gap-3">
                <ul className="flex items-center gap-2">
                  {slides.map((slide, index) => (
                    <li key={slide.id}>
                      <button
                        type="button"
                        aria-label={`${index + 1}. kampanyaya git: ${slide.title}`}
                        aria-current={index === active}
                        aria-controls={trackId}
                        onClick={() => {
                          holdAutoplay();
                          goTo(index);
                        }}
                        className={cn(
                          'block h-1.5 rounded-pill transition-all duration-base ease-flow',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700',
                          index === active
                            ? 'w-7 bg-leaf-700'
                            : 'w-1.5 bg-line hover:bg-leaf-300',
                        )}
                      />
                    </li>
                  ))}
                </ul>

                {/*
                  Kendiliğinden hareket eden içeriğin durdurulabilir olması
                  bir erişilebilirlik gereğidir (WCAG 2.2.2).
                */}
                <button
                  type="button"
                  onClick={() => setAutoplay((value) => !value)}
                  aria-label={
                    autoplay
                      ? 'Kampanyaların otomatik geçişini durdur'
                      : 'Kampanyaların otomatik geçişini başlat'
                  }
                  className={cn(
                    'inline-flex size-7 items-center justify-center rounded-full text-ink-muted',
                    'transition-colors duration-fast hover:bg-surface-sunken hover:text-ink',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700',
                  )}
                >
                  {autoplay ? (
                    <Pause className="size-3.5" aria-hidden />
                  ) : (
                    <Play className="size-3.5" aria-hidden />
                  )}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * Tek kampanya banner'ı.
 *
 * Görsel oranı sabit kaplarla verilir: mobilde dikey (4:5), masaüstünde
 * geniş (16:6). Böylece görsel yüklenmeden de yükseklik bilinir ve sayfa
 * zıplamaz (layout shift yok).
 *
 * Mobil görsel tanımlanmadıysa masaüstü görseli merkezden kırpılır — kampanya
 * yayınlamak için iki görsel hazırlamak zorunlu değildir.
 */
function CampaignBanner({
  slide,
  priority,
}: {
  slide: CampaignSlide;
  priority: boolean;
}) {
  const content = (
    <>
      <picture>
        {slide.mobileImageUrl ? (
          <source media="(max-width: 767px)" srcSet={slide.mobileImageUrl} />
        ) : null}
        <img
          src={slide.imageUrl}
          alt={slide.imageAlt}
          width={slide.imageWidth ?? undefined}
          height={slide.imageHeight ?? undefined}
          // İlk kampanya hero'nun hemen altındadır; onunla LCP yarışına
          // girmemesi için öncelik istenmez, yalnızca ertelenmez.
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          className="absolute inset-0 size-full object-cover object-center"
        />
      </picture>

      {/*
        Ağır bir gradient tüm görseli boğar. Perde yalnızca metnin durduğu
        alt bölgede ve okunabilirlik için gerektiği kadar.
      */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-ink/70 via-ink/25 to-transparent"
      />

      <div className="relative flex h-full flex-col justify-end p-5 sm:p-8 lg:p-10">
        <div className="max-w-xl">
          {slide.badge ? (
            <span className="inline-flex items-center rounded-pill bg-white/90 px-3 py-1 text-2xs font-medium uppercase tracking-wide text-ink backdrop-blur-md">
              {slide.badge}
            </span>
          ) : null}

          <h3
            className={cn(
              'font-serif text-2xl leading-tight text-white sm:text-3xl lg:text-4xl',
              slide.badge ? 'mt-3' : null,
            )}
          >
            {slide.title}
          </h3>

          {slide.description ? (
            <p className="mt-2 max-w-md text-sm text-white/90 sm:text-base">
              {slide.description}
            </p>
          ) : null}

          {slide.ctaLabel && slide.ctaHref ? (
            <span className="mt-5 inline-block sm:mt-6">
              <CampaignCta label={slide.ctaLabel} href={slide.ctaHref} />
            </span>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg bg-surface-sunken',
        // Oran mobilde dikey, masaüstünde geniş banner.
        'aspect-[4/5] min-[420px]:aspect-[3/4] sm:aspect-[16/7] lg:aspect-[16/6]',
      )}
    >
      {content}
    </div>
  );
}

/**
 * Kampanya butonu.
 *
 * Site içi hedefler yönlendirmesiz (router) gider; izin verilen dış adresler
 * yeni sekmede ve sekme kaçırmaya kapalı açılır. Hedefin güvenliğine sunucu
 * karar verdi; burada yalnızca doğru etiket seçilir.
 */
function CampaignCta({ label, href }: { label: string; href: string }) {
  if (href.startsWith('/')) {
    return (
      <Button asChild variant="onImage" size="md">
        <Link to={href}>
          {label}
          <ArrowRight className="size-4" aria-hidden />
        </Link>
      </Button>
    );
  }

  return (
    <Button asChild variant="onImage" size="md">
      <a href={href} target="_blank" rel="noopener noreferrer">
        {label}
        <ArrowRight className="size-4" aria-hidden />
      </a>
    </Button>
  );
}

// --- Ürün rayı -------------------------------------------------------------

export function ProductRail({
  section,
  delivery,
}: {
  section: ProductRailSection;
  delivery: DeliveryAvailability | null;
}) {
  const railRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollControls = useCallback(() => {
    const rail = railRef.current;

    if (!rail) return;

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    setCanScrollLeft(rail.scrollLeft > 2);
    setCanScrollRight(maxScrollLeft - rail.scrollLeft > 2);
  }, []);

  useEffect(() => {
    updateScrollControls();
    window.addEventListener('resize', updateScrollControls);

    return () => window.removeEventListener('resize', updateScrollControls);
  }, [section.products.length, updateScrollControls]);

  const scrollRail = (direction: -1 | 1) => {
    const rail = railRef.current;

    if (!rail) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.82, 320),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const railId = `product-rail-${section.key}`;

  return (
    <section
      className="mt-16 sm:mt-24"
      aria-labelledby={`rail-${section.key}-title`}
    >
      <div className="container-page flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h2
            id={`rail-${section.key}-title`}
            className="font-serif text-2xl text-ink sm:text-3xl"
          >
            {section.title}
          </h2>

          {/*
            Teslimat henüz yapılandırılmamışsa (yeni kurulum) geri sayım
            yerine bölümün kendi alt başlığı gösterilir — uydurma bir
            teslimat sözü verilmez.
          */}
          {section.showCountdown && delivery ? (
            delivery.sameDay.eligible ? (
              <p className="mt-1.5 text-sm text-ink-muted">
                Bugün teslim için{' '}
                <Countdown
                  cutoffAt={delivery.sameDay.cutoffAt}
                  className="font-medium text-leaf-700"
                />
              </p>
            ) : (
              // Bölge aynı güne kapalıysa bunu saklamak yerine söyleriz:
              // kullanıcı checkout'ta sürprizle karşılaşmamalı.
              <p className="mt-1.5 text-sm text-ink-muted">
                {delivery.zone.name} için en erken{' '}
                {delivery.earliestDateKey
                  ? formatDateKey(delivery.earliestDateKey)
                  : 'ileri bir tarih'}
              </p>
            )
          ) : section.subtitle ? (
            <p className="mt-1.5 text-sm text-ink-muted">{section.subtitle}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="hidden items-center gap-2 sm:flex" aria-label={`${section.title} ürünlerini kaydır`}>
            <button
              type="button"
              aria-label={`${section.title} ürünlerinde sola kaydır`}
              aria-controls={railId}
              disabled={!canScrollLeft}
              onClick={() => scrollRail(-1)}
              className="inline-flex size-9 items-center justify-center rounded-full border border-line bg-white text-ink transition-colors duration-fast hover:border-leaf-500 hover:text-leaf-700 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={`${section.title} ürünlerinde sağa kaydır`}
              aria-controls={railId}
              disabled={!canScrollRight}
              onClick={() => scrollRail(1)}
              className="inline-flex size-9 items-center justify-center rounded-full border border-line bg-white text-ink transition-colors duration-fast hover:border-leaf-500 hover:text-leaf-700 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight className="size-4" aria-hidden />
            </button>
          </div>
          <Link
            to={section.href}
            className="shrink-0 whitespace-nowrap text-sm text-leaf-600 transition-colors duration-fast hover:text-leaf-800"
          >
            Tümü
            <ArrowRight className="ml-1 inline size-3.5" aria-hidden />
          </Link>
        </div>
      </div>

      <div className="container-page mt-6">
        <ul
          ref={railRef}
          id={railId}
          className="rail gap-4"
          aria-label={section.title ?? 'Ürünler'}
          onScroll={updateScrollControls}
        >
          {section.products.map((product) => (
            <li
              key={product.id}
              className="w-[62%] min-[420px]:w-[46%] sm:w-[34%] md:w-[26%] lg:w-[21%]"
            >
              <ProductCard product={product} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// --- Editorial -------------------------------------------------------------

export function Editorial({ section }: { section: EditorialSection }) {
  return (
    <section className="mt-20 sm:mt-28">
      <div className="container-page">
        <div
          className={cn(
            'grid items-center gap-8 md:grid-cols-2 md:gap-14',
            section.align === 'right' && 'md:[&>*:first-child]:order-2',
          )}
        >
          <div className="overflow-hidden rounded-lg bg-surface-sunken">
            {section.imageUrl ? (
              <img
                src={section.imageUrl}
                alt={section.imageAlt ?? ''}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
            ) : null}
          </div>

          {/* Dar metin bloğu — okunabilirlik için ölçü sınırlı */}
          <div className="max-w-prose">
            <h2 className="font-serif text-2xl text-ink sm:text-3xl">
              {section.title}
            </h2>
            {section.subtitle ? (
              <p className="mt-4 text-md leading-normal text-ink-muted">
                {section.subtitle}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Kategori ızgarası (asimetrik) -----------------------------------------

export function CategoryGrid({ section }: { section: CategoryGridSection }) {
  return (
    <section className="mt-20 sm:mt-28" aria-labelledby="category-grid-title">
      <div className="container-page">
        <h2
          id="category-grid-title"
          className="font-serif text-2xl text-ink sm:text-3xl"
        >
          {section.title ?? 'Kategoriler'}
        </h2>

        {/* İlk iki kategori daha geniş: düz ızgara ritmini kırar. */}
        <ul className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {section.categories.map((category, index) => (
            <li
              key={category.id}
              className={cn(index < 2 && 'lg:col-span-2', 'min-w-0')}
            >
              <Link
                to={`/kategori/${category.slug}`}
                className="group block overflow-hidden rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700"
              >
                <div
                  className={cn(
                    'relative overflow-hidden bg-surface-sunken',
                    index < 2 ? 'aspect-[4/3] lg:aspect-[16/9]' : 'aspect-[4/3]',
                  )}
                >
                  {category.imageUrl ? (
                    <img
                      src={category.imageUrl}
                      alt={category.imageAlt ?? category.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-image ease-flow group-hover:scale-[1.04]"
                    />
                  ) : null}

                  <div
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/50 to-transparent"
                  />

                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <h3 className="font-serif text-lg text-white">{category.name}</h3>
                    <p className="mt-0.5 text-xs text-white/80">
                      {category.productCount} ürün
                    </p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// --- Yorumlar --------------------------------------------------------------

function Stars({ rating }: { rating: number }) {
  return (
    <span
      className="inline-flex gap-0.5"
      aria-label={`5 üzerinden ${rating} yıldız`}
    >
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          aria-hidden
          className={cn(
            'size-3.5',
            index < rating ? 'fill-gold text-gold' : 'text-line-strong',
          )}
        />
      ))}
    </span>
  );
}

export function Reviews({ section }: { section: ReviewsSection }) {
  return (
    <section className="mt-20 sm:mt-28" aria-labelledby="reviews-title">
      <div className="container-page">
        <h2 id="reviews-title" className="font-serif text-2xl text-ink sm:text-3xl">
          {section.title ?? 'Müşterilerimiz ne diyor?'}
        </h2>

        <ul className="mt-6 grid gap-4 md:grid-cols-3">
          {section.reviews.map((review) => (
            <li
              key={review.id}
              className="flex flex-col rounded-lg border border-line bg-surface p-5"
            >
              <Stars rating={review.rating} />

              <blockquote className="mt-3 flex-1 text-base leading-normal text-ink">
                {review.body}
              </blockquote>

              <footer className="mt-4 flex items-center gap-3 border-t border-line pt-4">
                {review.product?.imageUrl ? (
                  <img
                    src={review.product.imageUrl}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    className="size-10 shrink-0 rounded-sm object-cover"
                  />
                ) : null}

                <div className="min-w-0">
                  <p className="text-sm text-ink">{review.authorName}</p>
                  {review.product ? (
                    <Link
                      to={`/urun/${review.product.slug}`}
                      className="line-clamp-1 text-xs text-ink-muted transition-colors hover:text-leaf-600"
                    >
                      {review.product.name}
                    </Link>
                  ) : null}
                </div>
              </footer>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// --- Instagram -------------------------------------------------------------

export function InstagramWall({ section }: { section: InstagramSection }) {
  return (
    <section className="mt-20 sm:mt-28" aria-labelledby="instagram-title">
      <div className="container-page flex items-end justify-between gap-4">
        <h2 id="instagram-title" className="font-serif text-2xl text-ink sm:text-3xl">
          {section.handle ?? 'Instagram'}
        </h2>

        {section.profileUrl ? (
          <a
            href={section.profileUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm text-leaf-600 transition-colors hover:text-leaf-800"
          >
            <Instagram className="size-4" aria-hidden />
            Takip et
          </a>
        ) : null}
      </div>

      <ul className="container-page mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        {section.posts.map((post) => (
          <li key={post.id}>
            <a
              href={post.permalink}
              target="_blank"
              rel="noreferrer noopener"
              className="group block overflow-hidden rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700"
            >
              <img
                src={post.imageUrl}
                alt={post.alt ?? 'Instagram gönderisi'}
                loading="lazy"
                decoding="async"
                className="aspect-square w-full bg-surface-sunken object-cover transition-transform duration-image ease-flow group-hover:scale-[1.05]"
              />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Güven şeridi ----------------------------------------------------------

export function Trust({ section }: { section: TrustSection }) {
  return (
    <section className="mt-20 border-y border-line bg-surface sm:mt-28">
      <ul className="container-page grid gap-x-8 gap-y-6 py-10 sm:grid-cols-2 lg:grid-cols-4">
        {section.items.map((item) => (
          <li key={item.label}>
            <p className="text-sm font-medium text-ink">{item.label}</p>
            <p className="mt-1 text-sm text-ink-muted">{item.detail}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Özel gün bandı --------------------------------------------------------

export function OccasionBanner({ section }: { section: OccasionSection }) {
  return (
    <section className="mt-16 sm:mt-24">
      <div className="container-page">
        <Link
          to={section.href}
          className="flex flex-wrap items-center justify-between gap-4 rounded-lg px-6 py-8 transition-opacity duration-base hover:opacity-95"
          style={{ backgroundColor: section.accent ?? undefined }}
        >
          <div>
            <p className="text-xs uppercase tracking-wide text-white/80">
              {section.occasionName}
            </p>
            <h2 className="mt-2 font-serif text-2xl text-white">
              {section.title ?? section.occasionName}
            </h2>
            {section.subtitle ? (
              <p className="mt-1.5 text-sm text-white/85">{section.subtitle}</p>
            ) : null}
          </div>

          <span className="inline-flex items-center gap-1.5 rounded-sm bg-white/90 px-4 py-2 text-sm text-ink">
            Koleksiyonu gör
            <ArrowRight className="size-3.5" aria-hidden />
          </span>
        </Link>
      </div>
    </section>
  );
}
